/**
 * Input fields to values. Each parser answers `undefined` for text it cannot accept, so a form can tell
 * "left empty" (`null`) from "typed wrong" (`undefined`).
 */

/** A whole, non-negative number of pieces — quantities are never fractional (BR-16). */
export const parseWholeNumber = (text: string): number | undefined => {
  const trimmed = text.trim();

  return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
};

/** Empty is "no limit"; otherwise a whole number (BR-07). */
export const parseOptionalWholeNumber = (text: string): number | null | undefined =>
  text.trim() === '' ? null : parseWholeNumber(text);

/** "12,50" or "12.50" to 1250 minor units. Empty is "no price". */
export const parseMoneyInput = (text: string): number | null | undefined => {
  const normalised = text.trim().replace(',', '.');

  if (normalised === '') {
    return null;
  }

  return /^\d+(\.\d{1,2})?$/.test(normalised) ? Math.round(Number(normalised) * 100) : undefined;
};

export const moneyInputValue = (minor: number | null): string =>
  minor === null ? '' : (minor / 100).toFixed(2);

/**
 * "0,5" or "0.125" to a quantity of a unit (R0030 WP-07): above zero, at most three decimals. Empty is "no unit
 * price".
 */
export const parseUnitQuantity = (text: string): number | null | undefined => {
  const normalised = text.trim().replace(',', '.');

  if (normalised === '') {
    return null;
  }

  return /^\d+(\.\d{1,3})?$/.test(normalised) && Number(normalised) > 0 ? Number(normalised) : undefined;
};

export const unitQuantityInputValue = (quantity: number | null): string =>
  quantity === null ? '' : String(quantity);

const pad = (value: number): string => String(value).padStart(2, '0');

/** An ISO instant to the value of an `<input type="date">`, in local time. */
export const toDateInputValue = (iso: string | null): string => {
  if (!iso) {
    return '';
  }

  const date = new Date(iso);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const todayInputValue = (): string => toDateInputValue(new Date().toISOString());

/** The first instant of a picked day. */
export const startOfDayIso = (date: string): string => new Date(`${date}T00:00:00`).toISOString();

/** The last instant of a picked day: "valid until 31 Dec" includes the 31st. */
export const endOfDayIso = (date: string): string => new Date(`${date}T23:59:59.999`).toISOString();
